import React, { useState } from 'react';
import s from './s.modules.less';
import { Button, Input, message, Modal, Select } from 'antd';
import Stripe from 'assets/common/stripe.png';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import countries from 'constants/country';
import { emailRegex } from 'pages/SalesProviderExp/constant';
import { getSecretForBankPay } from 'api/channel';
import { savePaymentInfo } from 'api/ppaOnboarding';

type Props = {
    open?: boolean;
    onClose?: () => void;
    price?: number;
    onUpdatePaymentInfo?: () => void;
    hideBank?: boolean;
    embedded?: boolean;
    submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
    hidePaymentChangeMessageTips?: boolean;
};

const filedName = {
    cardNumber: 'cardNumber',
    cardExpiry: 'cardExpiry',
    cardCvc: 'cardCvc',
    email: 'email',
    country: 'country',
    zipCode: 'zipCode',
};

const EditPaymentModal = ({
    open,
    onClose,
    price,
    onUpdatePaymentInfo,
    hideBank,
    embedded,
    submitRef,
    hidePaymentChangeMessageTips,
}: Props) => {
    const [loadBasicData] = useLoadBasicData();
    const [checked, setChecked] = useState(false);
    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        setChecked(e.target.checked);
    };
    const [email, setEmail] = useState('');
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

    const [country, setCountry] = useState('US');
    const [zipCode, setZipCode] = useState('');
    const [showErrors, setShowErrors] = useState(false);

    const [loading, setLoading] = useState(false);

    const [showEdit, setShowEdit] = useState(false);
    const bankUsePaymentIntant = true;

    // 银行账户信息状态
    const [bankAccountInfo, setBankAccountInfo] = useState({
        accountHolderName: '',
        routingNumber: '',
        accountNumber: '',
        accountType: 'checking',
        country: 'US',
        zipCode: '',
    });

    const [fieldsVerifyResult, setFieldsVerifyResult] = useState({
        [filedName.cardNumber]: {
            error: true,
            message: 'Card number is required',
        },
        [filedName.cardExpiry]: {
            error: true,
            message: 'Card expiry date is required',
        },
        [filedName.cardCvc]: {
            error: true,
            message: 'Card CVC is required',
        },
        [filedName.email]: {
            error: false,
            message: 'Please enter the correct email',
        },
        [filedName.zipCode]: {
            error: true,
            message: 'This field is required',
        },
        [filedName.country]: {
            error: false,
            message: 'This field is required',
        },
    });

    const handleInputChange = (e: any, t: string) => {
        if (t === filedName.cardCvc || t === filedName.cardExpiry || t === filedName.cardNumber) {
            // e.complete && e.error (e.error.message)
            const correct = e.complete && !e.error;
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !correct,
                    message: correct ? '' : e.error?.message,
                },
            });
        } else if (t === filedName.country) {
            setCountry(e);
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !e,
                    message: e ? '' : 'This field is required',
                },
            });
        } else if (t === filedName.email) {
            setEmail(e);
            const emailVerify = emailRegex.test(e);
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !emailVerify,
                    message: emailVerify ? '' : 'Please enter the correct email',
                },
            });
        } else {
            if (t === filedName.zipCode) {
                setZipCode(e);
            }
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !e,
                    message: e ? '' : 'This field is required',
                },
            });
        }
    };

    // 银行账户输入处理函数
    const handleBankInputChange = (field: string, value: string) => {
        setBankAccountInfo((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleConfirm = async () => {
        // if (!email || !selectedPaymentMethod) {
        //     alert('Please fill in all required fields');
        //     return;
        // }

        setShowErrors(true);

        if (!elements || !stripe) {
            return;
        }

        if (selectedPaymentMethod === 'card') {
            if (Object.values(fieldsVerifyResult).every((e) => e.error)) {
                return;
            }
            const cardElement = elements.getElement(CardNumberElement);
            setLoading(true);
            const res = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement!,
                billing_details: {
                    email: email || '',
                    address: {
                        country,
                        postal_code: zipCode,
                    },
                },
            });
            if (!res.error) {
                const paymentMethodId = res.paymentMethod?.id;
                const res2 = await savePaymentInfo({
                    paymentMethodId,
                });
                if (!res2.error) {
                    if (!hidePaymentChangeMessageTips) {
                        message.success(
                            'Payment method updated successfully. Please note that it may take some time for the changes to take effect.',
                        );
                    }
                    onUpdatePaymentInfo?.();
                }
            }
            setLoading(false);
        } else if (selectedPaymentMethod === 'bank') {
            try {
                if (!bankAccountInfo.accountHolderName || !bankAccountInfo.country || !bankAccountInfo.zipCode) {
                    return;
                }

                //get clientSecret from backend
                setLoading(true);
                const resSecret = await getSecretForBankPay({
                    email,
                    country: bankAccountInfo.country,
                    postalCode: bankAccountInfo.zipCode,
                });
                if (!resSecret.error) {
                    const clientSecret = resSecret.data?.data;
                    const res = await stripe.collectBankAccountForSetup({
                        clientSecret,
                        params: {
                            payment_method_type: 'us_bank_account',
                            payment_method_data: {
                                billing_details: {
                                    name: bankAccountInfo.accountHolderName,
                                    email,
                                },
                            },
                        },
                    });
                    if (res && res.setupIntent && res.setupIntent.payment_method) {
                        const paymentMethodId = res.setupIntent.payment_method as string;
                        if (res.setupIntent.status === 'requires_confirmation') {
                            const confirmRes = await stripe.confirmUsBankAccountSetup(clientSecret, {
                                payment_method: paymentMethodId,
                                mandate_data: {
                                    customer_acceptance: {
                                        type: 'online',
                                        online: { infer_from_client: true },
                                    },
                                },
                            } as any);
                            console.log({
                                confirmRes,
                            });
                            if (confirmRes.error) {
                                message.error(
                                    confirmRes.error.message || 'Bank account confirmation failed. Please try again.',
                                );
                                return;
                            }
                            // 重新取确认结果
                            if (
                                !confirmRes.setupIntent ||
                                (confirmRes.setupIntent.status !== 'succeeded' &&
                                    confirmRes.setupIntent.status !== 'processing')
                            ) {
                                message.error('Bank account confirmation failed');
                                return;
                            }
                        }
                        const res2 = await savePaymentInfo({
                            paymentMethodId: paymentMethodId!,
                        });
                        if (!res2.error) {
                            message.success(
                                'Payment method updated successfully. Please note that it may take some time for the changes to take effect.',
                            );
                            onUpdatePaymentInfo?.();
                        }
                    }
                }
                // const res = await stripe.collectBankAccountForPayment(
                //     {
                //         clientSecret: 'pi_3RsKtVLWCWKPYTPj1UtXXVDL_secret_u5IWu2xRbwVvvHwOWZ9N9enUE',
                //         params: {
                //             payment_method_type: 'us_bank_account',
                //             payment_method_data: {
                //                 billing_details: {
                //                     name: bankAccountInfo.accountHolderName,
                //                     email,
                //                 },
                //             },
                //         },
                //     });
            } catch (e) {
                console.error('Error creating bank PaymentMethod:', e);
            } finally {
                setLoading(false);
            }

            // try {
            //     // 验证银行账户信息完整性
            //     if (!bankAccountInfo.accountHolderName ||
            //         !bankAccountInfo.routingNumber ||
            //         !bankAccountInfo.accountNumber ||
            //         !bankAccountInfo.accountType ||
            //         !bankAccountInfo.country ||
            //         !bankAccountInfo.zipCode
            //     ) {
            //         return;
            //     }
            //     setLoading(true);
            //     // 创建银行账户 PaymentMethod
            //     const result = await stripe.createPaymentMethod({
            //         type: 'us_bank_account',
            //         us_bank_account: {
            //             routing_number: bankAccountInfo.routingNumber,
            //             account_number: bankAccountInfo.accountNumber,
            //             account_holder_type: 'individual', // 或 'company'
            //             account_type: bankAccountInfo.accountType as 'checking' | 'savings',
            //         },
            //         billing_details: {
            //             name: bankAccountInfo.accountHolderName,
            //             email,
            //         },
            //     });

            //     if (result.error) {
            //         message.error('Bank payment creation failed');
            //     } else {
            //         const paymentMethodId = result.paymentMethod?.id;
            //         const res2 = await channelPay({
            //             channelId: channel?.id!,
            //             paymentMethodId,
            //             email,
            //             country: bankAccountInfo.country,
            //             postalCode: bankAccountInfo.zipCode,
            //         });
            //         if (!res2.error) {
            //             navigate(`${PATH.CHANNEL_PAYMENT_SUCCESS}`);
            //         }
            //     }
            //     setLoading(false);
            // } catch (error) {
            //     console.error('Error creating bank PaymentMethod:', error);
            // }
        }
    };

    if (submitRef) {
        submitRef.current = handleConfirm;
    }

    const formContent = (
        <div className={s.wrap} style={embedded ? { maxHeight: 'none', paddingRight: 0 } : undefined}>
            {!embedded && (
                <div className={s.title}>
                    Edit payment information
                    <img src={Stripe} />
                </div>
            )}
            <div className={s.content}>
                <div className={s.formGroup}>
                    <label className={s.label}>Payment method</label>
                    <div className={s.paymentMethods}>
                        {!hideBank && (
                            <>
                                <div
                                    className={`${s.paymentOption} ${selectedPaymentMethod === 'bank' ? s.selected : ''}`}
                                    onClick={() => setSelectedPaymentMethod('bank')}
                                >
                                    <div className={s.radioButton}>
                                        {selectedPaymentMethod === 'bank' && <div className={s.radioSelected} />}
                                    </div>
                                    <div className={s.paymentIcon}>
                                        <div className={s.bankIcon}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M5.00031 7.5V14H6.50031V7.5H9.50031V14H11.0003V7.5H14.0003V14H15.0003C15.2655 14 15.5199 14.1054 15.7074 14.2929C15.895 14.4804 16.0003 14.7348 16.0003 15V16H0.000313816V15C0.000313816 14.7348 0.105671 14.4804 0.293207 14.2929C0.480743 14.1054 0.735097 14 1.00031 14H2.00031V7.5H5.00031ZM8.00031 0C12.6813 2.572 15.1813 3.95 15.5003 4.134C15.6925 4.24488 15.8424 4.41655 15.9263 4.62197C16.0103 4.82739 16.0235 5.05489 15.9639 5.26865C15.9044 5.48241 15.7754 5.67028 15.5973 5.80269C15.4192 5.93509 15.2022 6.00451 14.9803 6H1.02031C0.798455 6.00451 0.581399 5.93509 0.403324 5.80269C0.22525 5.67028 0.0962732 5.48241 0.0367046 5.26865C-0.0228641 5.05489 -0.00964091 4.82739 0.0742918 4.62197C0.158224 4.41655 0.308099 4.24488 0.500314 4.134C0.820314 3.95 3.32031 2.572 8.00031 0Z"
                                                    fill="#15191E"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <span className={s.paymentLabel}>Bank</span>
                                </div>
                                {selectedPaymentMethod === 'bank' && (
                                    <div className={s.bankWrap}>
                                        <div className={s.bankDisclaimer}>
                                            <div className={s.disclaimerIcon}>⚠️</div>
                                            <div className={s.disclaimerText}>
                                                Bank accounts require verification via microdeposits, which may take 1-2
                                                business days.
                                            </div>
                                        </div>
                                        <div className={s.mandateText}>
                                            By saving your bank account and clicking Save, you authorize Klarity to
                                            electronically debit the account you provided for any amount owed for
                                            charges arising from your use of Klarity&apos;s services and/or purchase of
                                            products from Klarity, pursuant to Klarity&apos;s website and terms, until
                                            this authorization is revoked. You may amend or cancel this authorization at
                                            any time by providing notice to Klarity with 30 (thirty) days notice.
                                        </div>
                                        <div className={s.formRow}>
                                            <div className={s.formItem}>
                                                <div className={s.label}>Account holder name</div>
                                                <div className={s.inputBox}>
                                                    <Input
                                                        value={bankAccountInfo.accountHolderName}
                                                        onChange={(e) =>
                                                            handleBankInputChange('accountHolderName', e.target.value)
                                                        }
                                                        placeholder="Full name on account"
                                                    />
                                                </div>
                                                {showErrors && !bankAccountInfo.accountHolderName && (
                                                    <div className={s.error}>Account holder name is required</div>
                                                )}
                                            </div>
                                        </div>
                                        {!bankUsePaymentIntant && (
                                            <>
                                                <div className={s.formRow}>
                                                    <div className={s.formItem}>
                                                        <div className={s.label}>Routing number</div>
                                                        <div className={s.inputBox}>
                                                            <Input
                                                                value={bankAccountInfo.routingNumber}
                                                                onChange={(e) =>
                                                                    handleBankInputChange(
                                                                        'routingNumber',
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="9-digit routing number"
                                                                maxLength={9}
                                                            />
                                                        </div>
                                                        {showErrors && !bankAccountInfo.routingNumber && (
                                                            <div className={s.error}>Routing number is required</div>
                                                        )}
                                                    </div>
                                                    <div className={s.formItem}>
                                                        <div className={s.label}>Account number</div>
                                                        <div className={s.inputBox}>
                                                            <Input
                                                                value={bankAccountInfo.accountNumber}
                                                                onChange={(e) =>
                                                                    handleBankInputChange(
                                                                        'accountNumber',
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="Account number"
                                                                maxLength={17}
                                                            />
                                                        </div>
                                                        {showErrors && !bankAccountInfo.accountNumber && (
                                                            <div className={s.error}>Account number is required</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={s.formRow}>
                                                    <div className={s.formItem}>
                                                        <div className={s.label}>Account type</div>
                                                        <div className={s.inputBox}>
                                                            <Select
                                                                value={bankAccountInfo.accountType}
                                                                onChange={(value) =>
                                                                    handleBankInputChange('accountType', value)
                                                                }
                                                                options={[
                                                                    { label: 'Checking', value: 'checking' },
                                                                    { label: 'Savings', value: 'savings' },
                                                                ]}
                                                                placeholder="Select account type"
                                                                className={s.select}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className={s.formRow}>
                                            <div className={s.formItem}>
                                                <div className={s.label}>Country</div>
                                                <div className={s.inputBox}>
                                                    <Select
                                                        showSearch
                                                        filterOption={(inputValue, option) =>
                                                            option?.label
                                                                ?.toLowerCase()
                                                                .indexOf(inputValue.toLowerCase()) >= 0
                                                        }
                                                        value={bankAccountInfo.country}
                                                        options={countries}
                                                        className={s.select}
                                                        onChange={(e) => {
                                                            handleBankInputChange('country', e);
                                                        }}
                                                    />
                                                </div>
                                                {showErrors && !bankAccountInfo.country && (
                                                    <div className={s.error}>Country is required</div>
                                                )}
                                            </div>
                                            <div className={s.formItem}>
                                                <div className={s.label}>Postal code</div>
                                                <div className={s.inputBox}>
                                                    <Input
                                                        value={bankAccountInfo.zipCode}
                                                        type="number"
                                                        maxLength={8}
                                                        onChange={(e) => {
                                                            handleBankInputChange('zipCode', e.target.value);
                                                        }}
                                                    />
                                                </div>
                                                {showErrors && !bankAccountInfo.zipCode && (
                                                    <div className={s.error}>Postal code is required</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        <div
                            className={`${s.paymentOption} ${selectedPaymentMethod === 'card' ? s.selected : ''}`}
                            onClick={() => setSelectedPaymentMethod('card')}
                        >
                            {!hideBank && (
                                <div className={s.radioButton}>
                                    {selectedPaymentMethod === 'card' && <div className={s.radioSelected} />}
                                </div>
                            )}
                            <div className={s.paymentIcon}>
                                <div className={s.cardIcon}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="12"
                                        viewBox="0 0 16 12"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M0 2C0 1.46957 0.210714 0.960859 0.585786 0.585786C0.960859 0.210714 1.46957 0 2 0H14C14.5304 0 15.0391 0.210714 15.4142 0.585786C15.7893 0.960859 16 1.46957 16 2H0ZM0 4V10C0 10.5304 0.210714 11.0391 0.585786 11.4142C0.960859 11.7893 1.46957 12 2 12H14C14.5304 12 15.0391 11.7893 15.4142 11.4142C15.7893 11.0391 16 10.5304 16 10V4H0ZM3 9C3 8.73478 3.10536 8.48043 3.29289 8.29289C3.48043 8.10536 3.73478 8 4 8H5C5.26522 8 5.51957 8.10536 5.70711 8.29289C5.89464 8.48043 6 8.73478 6 9C6 9.26522 5.89464 9.51957 5.70711 9.70711C5.51957 9.89464 5.26522 10 5 10H4C3.73478 10 3.48043 9.89464 3.29289 9.70711C3.10536 9.51957 3 9.26522 3 9Z"
                                            fill="#15191E"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <span className={s.paymentLabel}>Card</span>
                            <div className={s.cardBrands}>
                                {selectedPaymentMethod !== 'card' && (
                                    <div className={s.cardIconList}>
                                        <div className={`${s.card} ${s.mastercard}`} />
                                        <div className={`${s.card} ${s.amex}`} />
                                        <div className={`${s.card} ${s.discover}`} />
                                        <div className={`${s.card} ${s.visa}`} />
                                    </div>
                                )}
                            </div>
                        </div>
                        {selectedPaymentMethod === 'card' && (
                            <div className={s.cardWrap}>
                                <div className={s.cardTips}>
                                    A 3% processing fee applies when paying by credit card.
                                </div>
                                <div className={s.formRow}>
                                    <div className={s.formItem}>
                                        <div className={s.label}>Card Number</div>
                                        <div className={s.inputBox}>
                                            <div className={s.inputBoxInner}>
                                                <CardNumberElement
                                                    className={`${s.input} ${s.inputWithoutBorder}`}
                                                    onChange={(e) => handleInputChange(e, filedName.cardNumber)}
                                                />
                                                <div className={s.cardIconList}>
                                                    <div className={`${s.card} ${s.mastercard}`} />
                                                    <div className={`${s.card} ${s.amex}`} />
                                                    <div className={`${s.card} ${s.discover}`} />
                                                    <div className={`${s.card} ${s.visa}`} />
                                                </div>
                                            </div>
                                        </div>
                                        {showErrors && fieldsVerifyResult[filedName.cardNumber].error && (
                                            <div className={s.error}>
                                                {fieldsVerifyResult[filedName.cardNumber].message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={s.formRow}>
                                    <div className={s.formItem}>
                                        <div className={s.label}>Expiration</div>
                                        <div className={s.inputBox}>
                                            <CardExpiryElement
                                                className={s.input}
                                                onChange={(e) => handleInputChange(e, filedName.cardExpiry)}
                                            />
                                        </div>
                                        {showErrors && fieldsVerifyResult[filedName.cardExpiry].error && (
                                            <div className={s.error}>
                                                {fieldsVerifyResult[filedName.cardExpiry].message}
                                            </div>
                                        )}
                                    </div>
                                    <div className={s.formItem}>
                                        <div className={s.label}>CVC</div>
                                        <div className={s.inputBox}>
                                            <CardCvcElement
                                                className={s.input}
                                                onChange={(e) => handleInputChange(e, filedName.cardCvc)}
                                            />
                                        </div>
                                        {showErrors && fieldsVerifyResult[filedName.cardCvc].error && (
                                            <div className={s.error}>
                                                {fieldsVerifyResult[filedName.cardCvc].message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={s.formRow}>
                                    <div className={s.formItem}>
                                        <div className={s.label}>Country</div>
                                        <div className={s.inputBox}>
                                            <Select
                                                showSearch
                                                filterOption={(inputValue, option) =>
                                                    option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                                }
                                                value={country}
                                                options={countries}
                                                className={s.select}
                                                onChange={(e) => handleInputChange(e, filedName.country)}
                                            />
                                        </div>
                                        {showErrors && fieldsVerifyResult[filedName.country].error && (
                                            <div className={s.error}>
                                                {fieldsVerifyResult[filedName.country].message}
                                            </div>
                                        )}
                                    </div>
                                    <div className={s.formItem}>
                                        <div className={s.label}>Postal code</div>
                                        <div className={s.inputBox}>
                                            <Input
                                                value={zipCode}
                                                type="number"
                                                onChange={(e) => handleInputChange(e.target.value, filedName.zipCode)}
                                            />
                                        </div>
                                        {showErrors && fieldsVerifyResult[filedName.zipCode].error && (
                                            <div className={s.error}>
                                                {fieldsVerifyResult[filedName.zipCode].message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (embedded) {
        return formContent;
    }

    return (
        <div className={s.out}>
            <Modal
                open={open}
                onCancel={onClose}
                width={600}
                destroyOnClose
                footer={
                    <div className={s.footer}>
                        <Button
                            onClick={handleConfirm}
                            className={s.btn}
                            type="primary"
                            block
                            size="large"
                            loading={loading}
                        >
                            Save
                        </Button>
                    </div>
                }
            >
                {formContent}
            </Modal>
        </div>
    );
};

export default EditPaymentModal;
